import { createContext, useContext, useEffect, useState } from 'react';

// Reference logical dimensions. Shapes use these as their coordinate space.
// Outer container fills viewport edge-to-edge with the slide background;
// inner logical content layer is CSS-scaled to fit (no black bars).
const LANDSCAPE_REF = { w: 1920, h: 1080 };
const PORTRAIT_REF = { w: 1080, h: 1920 };

interface StageDims {
  w: number;
  h: number;
  orientation: 'landscape' | 'portrait';
}

const StageDimsCtx = createContext<StageDims>({ ...LANDSCAPE_REF, orientation: 'landscape' });

export function useStageDims() {
  return useContext(StageDimsCtx);
}

interface StageProps {
  children: React.ReactNode;
  background?: string;
}

export function Stage({ children, background = '#ffffff' }: StageProps) {
  const [dims, setDims] = useState<StageDims>(() => initialDims());
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const fit = () => {
      const portrait = window.innerHeight > window.innerWidth;
      const ref = portrait ? PORTRAIT_REF : LANDSCAPE_REF;
      const s = Math.min(window.innerWidth / ref.w, window.innerHeight / ref.h);
      setDims({ ...ref, orientation: portrait ? 'portrait' : 'landscape' });
      setScale(s);
    };
    fit();
    window.addEventListener('resize', fit);
    window.addEventListener('orientationchange', fit);
    return () => {
      window.removeEventListener('resize', fit);
      window.removeEventListener('orientationchange', fit);
    };
  }, []);

  return (
    <StageDimsCtx.Provider value={dims}>
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background, // slide bg fills viewport edge-to-edge
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            width: dims.w,
            height: dims.h,
            marginLeft: -dims.w / 2,
            marginTop: -dims.h / 2,
            transform: `scale(${scale})`,
            transformOrigin: 'center center',
          }}
        >
          {children}
        </div>
      </div>
    </StageDimsCtx.Provider>
  );
}

function initialDims(): StageDims {
  if (typeof window === 'undefined') return { ...LANDSCAPE_REF, orientation: 'landscape' };
  const portrait = window.innerHeight > window.innerWidth;
  const ref = portrait ? PORTRAIT_REF : LANDSCAPE_REF;
  return { ...ref, orientation: portrait ? 'portrait' : 'landscape' };
}
